import { CASH_REGISTER_KIND_LABELS, type CashRegister } from "@station/contracts";
import {
  Alert,
  MoneyText,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../../features/accounting/company.js";
import { CashBookTab } from "../../../../features/treasury/cash-book.js";
import { CashCountsTab } from "../../../../features/treasury/cash-count.js";
import { TreasuryPage, useFinanceCrumb } from "../../../../features/treasury/shared.js";
import { TransactionsList } from "../../../../features/treasury/transactions-list.js";
import { api } from "../../../../lib/api.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDateTime } from "../../../../lib/format.js";
import { queryKeys } from "../../../../lib/query.js";

const TABS = ["book", "transactions", "counts"] as const;
type TabKey = (typeof TABS)[number];

const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/finans/kasalar/$id")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CashRegisterDetailPage,
});

function CashRegisterDetailPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const listCrumb = useFinanceCrumb("/finans/kasalar", "menu.cashRegisters");
  const register = useQuery({
    queryKey: queryKeys.treasury(null, "cash-register", id),
    queryFn: async () => (await api.GET("/cash-registers/{id}", { params: { path: { id } } })).data!,
  });

  return (
    <TreasuryPage
      title={register.data ? `${register.data.code} · ${register.data.name}` : t("menu.cashRegisters")}
      breadcrumb={[listCrumb, { label: register.data?.code ?? "…" }]}
    >
      {({ companyId }) =>
        register.error ? (
          <Alert tone="danger">{errorMessage(register.error)}</Alert>
        ) : !register.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <RegisterTabs companyId={companyId} register={register.data} />
        )
      }
    </TreasuryPage>
  );
}

function RegisterTabs({ companyId, register }: { companyId: string; register: CashRegister }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const tab: TabKey = Route.useSearch().tab ?? "book";

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <span className="flex items-baseline gap-2">
          <span className="text-muted-foreground">{t("treasury.fields.balance")}</span>
          <MoneyText value={register.balance} colorize className="text-lg font-semibold" />
        </span>
        <span className="flex items-baseline gap-2">
          <span className="text-muted-foreground">{t("treasury.fields.kind")}</span>
          <span className="font-medium">{CASH_REGISTER_KIND_LABELS[register.kind]}</span>
        </span>
        <span className="flex items-baseline gap-2">
          <span className="text-muted-foreground">{t("treasury.fields.station")}</span>
          <span className="font-medium">{register.stationName ?? t("treasury.fields.headOffice")}</span>
        </span>
        <span className="flex items-baseline gap-2">
          <span className="text-muted-foreground">{t("treasury.fields.ledgerAccount")}</span>
          <span className="tabular font-medium">{register.ledgerAccountCode ?? "—"}</span>
        </span>
        <span className="flex items-baseline gap-2">
          <span className="text-muted-foreground">{t("treasury.fields.lastCount")}</span>
          <span className="tabular font-medium">{formatDateTime(register.lastCountAt)}</span>
        </span>
        {register.isActive ? null : <StatusBadge tone="neutral">{t("common.inactive")}</StatusBadge>}
      </div>

      <Tabs
        value={tab}
        onValueChange={(next) =>
          void navigate({
            search: (previous) => ({ company: previous.company, tab: next as TabKey }),
            replace: true,
          })
        }
      >
        <TabsList aria-label={t("treasury.cashRegisters.tabsLabel")} className="flex-wrap">
          {TABS.map((key) => (
            <TabsTrigger key={key} value={key}>
              {t(`treasury.cashRegisters.tabs.${key}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="book">
          {tab === "book" ? <CashBookTab companyId={companyId} cashRegisterId={register.id} /> : null}
        </TabsContent>
        <TabsContent value="transactions">
          {tab === "transactions" ? (
            <TransactionsList
              companyId={companyId}
              filter={{ cashRegisterId: register.id }}
              caption={t("treasury.cashRegisters.tabs.transactions")}
            />
          ) : null}
        </TabsContent>
        <TabsContent value="counts">
          {tab === "counts" ? <CashCountsTab companyId={companyId} register={register} /> : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
